import { getLocale } from "@umijs/max";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

export interface XhrDownloadRequestOptions {
  url: string;
  method?: HttpMethod;
  params?: Record<string, any>;
  data?: any;
  headers?: Record<string, string>;
  timeout?: number;
  onDownloadProgress?: (progress: {
    loaded: number;
    total: number;
    lengthComputable: boolean;
  }) => void;
}

export interface XhrDownloadResponse {
  data: Blob;
  response: {
    status: number;
    headers: Record<string, string>;
  };
}

const filterEmptyParams = (params?: Record<string, any>) => {
  if (!params) return {};
  const result: Record<string, any> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      result[key] = value;
    }
  });
  return result;
};

const toQueryString = (params?: Record<string, any>) => {
  const searchParams = new URLSearchParams();
  const normalizedParams = filterEmptyParams(params);
  Object.entries(normalizedParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, String(item)));
      return;
    }
    searchParams.append(key, String(value));
  });
  return searchParams.toString();
};

const parseXhrHeaders = (rawHeaders: string) => {
  const result: Record<string, string> = {};
  rawHeaders
    .trim()
    .split(/[\r\n]+/)
    .forEach((line) => {
      const index = line.indexOf(":");
      if (index <= 0) return;
      const key = line.slice(0, index).trim().toLowerCase();
      const value = line.slice(index + 1).trim();
      result[key] = value;
    });
  return result;
};

const hasContentTypeHeader = (headers: Record<string, string>) => {
  return Object.keys(headers).some(
    (key) => key.toLowerCase() === "content-type",
  );
};

const normalizeToError = (error: any) => {
  if (error instanceof Error) return error;
  return new Error("Network Error");
};

const requestByXhr = (
  requestUrl: string,
  method: HttpMethod,
  timeout: number,
  requestHeaders: Record<string, string>,
  payload: Document | XMLHttpRequestBodyInit | null,
  onDownloadProgress?: XhrDownloadRequestOptions["onDownloadProgress"],
) => {
  return new Promise<XhrDownloadResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, requestUrl, true);
    xhr.responseType = "blob";
    xhr.timeout = timeout;

    Object.entries(requestHeaders).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    xhr.onprogress = (event: ProgressEvent<EventTarget>) => {
      onDownloadProgress?.({
        loaded: event.loaded,
        total: event.total,
        lengthComputable: event.lengthComputable,
      });
    };

    xhr.onerror = () => {
      reject(new Error("Network Error"));
    };

    xhr.onabort = () => {
      reject(new Error("Network Error"));
    };

    xhr.ontimeout = () => {
      const timeoutError: any = new Error("timeout");
      timeoutError.code = "ECONNABORTED";
      reject(timeoutError);
    };

    xhr.onload = async () => {
      const responseHeaders = parseXhrHeaders(xhr.getAllResponseHeaders());
      const responseBlob =
        xhr.response instanceof Blob
          ? xhr.response
          : new Blob([xhr.response ?? ""], {
              type:
                responseHeaders["content-type"] || "application/octet-stream",
            });

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({
          data: responseBlob,
          response: {
            status: xhr.status,
            headers: responseHeaders,
          },
        });
        return;
      }

      reject(await buildBusinessError(responseBlob, `请求失败(${xhr.status})`));
    };

    xhr.send(payload);
  });
};

const buildBusinessError = async (
  responseBlob: Blob,
  fallbackMessage: string,
): Promise<Error> => {
  try {
    const text = await responseBlob.text();
    const json = JSON.parse(text);
    const message =
      json?.msg || json?.message || json?.errorMessage || fallbackMessage;
    return new Error(message);
  } catch {
    return new Error(fallbackMessage);
  }
};

const withApiBase = (url: string) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const apiBase = typeof API_URL === "string" ? API_URL : "";
  return `${apiBase}${url}`;
};

export const downloadByXhr = async (
  options: XhrDownloadRequestOptions,
): Promise<XhrDownloadResponse> => {
  const {
    url,
    method = "GET",
    params,
    data,
    headers,
    timeout = 600000,
    onDownloadProgress,
  } = options;

  const queryString = toQueryString(params);
  const requestUrl = withApiBase(
    queryString ? `${url}${url.includes("?") ? "&" : "?"}${queryString}` : url,
  );
  const token = localStorage.getItem("token");

  const requestHeaders: Record<string, string> = {
    Language: getLocale(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(headers || {}),
  };

  const isFormData =
    typeof FormData !== "undefined" && data instanceof FormData;
  const hasBody = data !== undefined && data !== null;
  const payload =
    hasBody && (typeof data === "string" || isFormData)
      ? data
      : hasBody
        ? JSON.stringify(data)
        : undefined;

  if (hasBody && !isFormData && !hasContentTypeHeader(requestHeaders)) {
    requestHeaders["Content-Type"] = "application/json";
  }

  try {
    const response = await requestByXhr(
      requestUrl,
      method,
      timeout,
      requestHeaders,
      (payload ?? null) as Document | XMLHttpRequestBodyInit | null,
      onDownloadProgress,
    );
    return response;
  } catch (error: any) {
    throw normalizeToError(error);
  }
};
